import { createFileRoute, Link } from "@tanstack/react-router";
import {
  GraduationCap,
  PenLine,
  Search,
  Gavel,
  Library,
  Briefcase,
  Users,
  ArrowRight,
  ShieldCheck,
} from "lucide-react";

export const Route = createFileRoute("/education/")({
  head: () => ({
    meta: [
      { title: "Education & Professional Development | Nkadimeng Danny" },
      {
        name: "description",
        content:
          "LLB tutoring, legal writing, legal research, Court Online training, downloadable study resources and legal-career development — all in one South African legal-learning hub.",
      },
      { property: "og:title", content: "Education & Professional Development" },
      {
        property: "og:description",
        content:
          "Tutoring, writing, research, Court Online and career development for South African law students and professionals.",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          name: "Nkadimeng Danny — Education & Professional Development",
          url: "https://nkadimengdannylcs.com/education",
          description:
            "Legal education, tutoring, professional development and legal-technology platform for South African students and professionals.",
        }),
      },
    ],
  }),
  component: EducationPage,
});

const pillars = [
  {
    to: "/resources",
    icon: GraduationCap,
    heading: "NDLCS Academy",
    body: "In-house skills resources on workplace rights, POPIA & compliance, personal finance and contract literacy.",
    bullets: [
      "Short, self-paced skills modules",
      "Professional-development certificates",
      "Curated study notes and guides",
    ],
    cta: "Open the academy",
  },
  {
    to: "/education/writing",
    icon: PenLine,
    heading: "Legal Writing Academy",
    body: "Train in legal essays, assignments, research papers, opinions, case notes and memoranda — with structured IRAC writing and citation in OSCOLA, Harvard, APA 7 and South African Legal Citation.",
    bullets: [
      "Citation guides for OSCOLA, Harvard, APA 7, SA Style",
      "AI-assisted citation and referencing",
      "Templates for opinions, memos and case notes",
    ],
    cta: "Open writing academy",
  },
  {
    to: "/education/research",
    icon: Search,
    heading: "Legal Research Hub",
    body: "Case analysis tools, IRAC generator, citation and footnote generators, research guides and judgment summaries — designed to accelerate study and practice.",
    bullets: [
      "IRAC and case-analysis generators",
      "Footnote and citation tools",
      "Outputs encourage primary-source verification",
    ],
    cta: "Open research hub",
  },
  {
    to: "/education/court-online",
    icon: Gavel,
    heading: "Court Online Training",
    body: "Practical training on the South African Court Online platform — registration, case initiation, document filing, uploads, service and digital litigation processes.",
    bullets: [
      "Video tutorials and step-by-step guides",
      "Short assessments per module",
      "Certificates of completion (educational)",
    ],
    cta: "Start training",
  },
  {
    to: "/resources",
    icon: Library,
    heading: "Legal Education Resource Library",
    body: "Downloadable study notes, templates, guides, checklists, summaries, examination tips and research resources — categorised by subject and topic.",
    bullets: [
      "Subject-by-subject filtering",
      "Free study notes for core LLB modules",
      "Templates for memos, opinions and letters",
    ],
    cta: "Browse the library",
  },
  {
    to: "/cv-services",
    icon: Briefcase,
    heading: "Career Development Centre",
    body: "CV development, LinkedIn optimisation, candidate-attorney preparation, interview coaching, professional branding and networking guidance for emerging legal professionals.",
    bullets: [
      "AI CV builder and interview coach",
      "Candidate-attorney preparation",
      "Professional branding playbooks",
    ],
    cta: "Build your career",
  },
  {
    to: "/education/high-school",
    icon: Users,
    heading: "High School Legal Literacy Initiative",
    body: "Community programme building legal literacy, compliance awareness and workplace readiness among young South Africans — debate leagues, free online courses, skills pathways and outreach workshops.",
    bullets: [
      "High School Legal Debate League",
      "Free NDLCS Academy online courses",
      "Skills development & community workshops",
    ],
    cta: "Explore the initiative",
  },
] as const;

const subjects = [
  "Constitutional Law",
  "Contract Law",
  "Criminal Law",
  "Administrative Law",
  "Labour Law",
  "Law of Delict",
  "Jurisprudence",
  "Law of Persons",
  "Civil Procedure",
  "Criminal Procedure",
  "Legal Interpretation",
  "Legal Research Methodology",
];

function EducationPage() {
  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="border-b border-border bg-gradient-to-b from-navy/5 to-transparent">
        <div className="container-narrow py-16 md:py-24">
          <p className="text-xs uppercase tracking-[0.32em] text-gold">
            Education & Professional Development Division
          </p>
          <h1 className="mt-3 font-display text-4xl leading-tight text-navy md:text-5xl">
            A legal-learning, professional-development and
            <span className="text-gold"> legal-technology</span> platform.
          </h1>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-foreground/80 md:text-lg">
            Empowering students, candidate attorneys, legal-support professionals and the
            public through knowledge, innovation and ethical service. Tutoring, writing,
            research, Court Online training and career development — built for the South
            African legal landscape.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/resources"
              className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-medium text-navy-foreground shadow-soft hover:bg-navy/90"
            >
              Browse the resource library <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/legislation"
              className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-5 py-3 text-sm font-medium hover:border-navy"
            >
              SA Legislation library
            </Link>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="container-narrow py-16">
        <h2 className="font-display text-2xl text-navy md:text-3xl">
          Six pillars of the Education Division
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Each pillar is a dedicated programme. Some are live now; others are rolling out
          progressively.
        </p>

        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {pillars.map((p) => (
            <Link
              key={p.heading}
              to={p.to as any}
              className="group flex flex-col rounded-xl border border-border bg-card p-6 transition-colors hover:border-navy"
            >
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-md bg-navy/5 text-navy">
                  <p.icon className="h-5 w-5" />
                </span>
                <h3 className="font-display text-lg text-navy">{p.heading}</h3>
                {"soon" in p && p.soon ? (
                  <span className="ml-auto rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gold">
                    Rolling out
                  </span>
                ) : null}
              </div>
              <p className="mt-3 text-sm leading-relaxed text-foreground/80">{p.body}</p>
              <ul className="mt-4 space-y-1.5 text-sm text-foreground/75">
                {p.bullets.map((b) => (
                  <li key={b} className="flex gap-2">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gold" />
                    {b}
                  </li>
                ))}
              </ul>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-navy group-hover:text-gold">
                {p.cta} <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Subjects */}
      <section className="border-y border-border bg-muted/30">
        <div className="container-narrow py-14">
          <h2 className="font-display text-2xl text-navy">Subjects we support</h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Twelve core LLB subjects covered through tutoring, study notes, writing
            workshops and research tools.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {subjects.map((s) => (
              <span
                key={s}
                className="rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground/80"
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Positioning */}
      <section className="container-narrow py-16">
        <div className="rounded-xl border border-navy/20 bg-navy p-8 text-navy-foreground md:p-12">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-1 h-5 w-5 text-gold" />
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-gold">Our positioning</p>
              <p className="mt-3 font-display text-xl leading-relaxed md:text-2xl">
                "A legal-support, compliance, legal-education, professional-development
                and legal-technology platform — dedicated to empowering students,
                professionals, businesses and the public through knowledge, innovation
                and ethical service."
              </p>
            </div>
          </div>
        </div>
        <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
          Training and educational materials provided through this Division are
          educational in nature and do not constitute legal advice, legal representation
          or official accreditation. Always verify outputs against primary sources and
          consult an admitted legal practitioner for matters requiring formal advice.
        </p>
      </section>
    </div>
  );
}
